import React, { useState, useEffect, useRef } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Box,
    Typography,
    Alert,
    Paper,
    Chip,
    Collapse,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'react-toastify';
import {
    journalEntriesService,
    JournalEntryDto,
    CreateJournalEntryDto,
    UpdateJournalEntryDto,
} from '../../services/journalEntries.service';
import AccountSelector from '../common/AccountSelector';
import CostCenterSelector from '../common/CostCenterSelector';
import EntryTypeSelector from '../common/EntryTypeSelector';
import FiscalYearSelector from '../common/FiscalYearSelector';
import { EntryTypeDto } from '../../types/entryTypes.types';
import apiClient from '../../services/api';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { useGridEnterNavigation } from '../../hooks/useGridEnterNavigation';

interface JournalEntryDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    entry?: JournalEntryDto;
}

interface DetailLine {
    id: string;
    lineNumber: number;
    accountId: number | null;
    debit: number;
    credit: number;
    costCenterId: number | null;
    description: string;
    notes: string;
    entryDetailId?: number;
}

const JournalEntryDialog: React.FC<JournalEntryDialogProps> = ({
    open,
    onClose,
    onSuccess,
    entry,
}) => {
    const { language } = useLanguage();
    const isRTL = language === 'ar';
    const isEdit = !!entry;
    const { minDate, maxDate } = useFiscalDateRange();

    const gridRef = useRef<HTMLDivElement>(null);
    useGridEnterNavigation(gridRef);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    // Master fields
    const [entryTypeId, setEntryTypeId] = useState<number | null>(null);
    const [entryType, setEntryType] = useState<EntryTypeDto | null>(null);
    const [entryNumberPrefix, setEntryNumberPrefix] = useState('JE-');
    const [entryNumber, setEntryNumber] = useState('');
    const [entryDate, setEntryDate] = useState('');
    const [fiscalPeriodId, setFiscalPeriodId] = useState<number | null>(null);
    const [description, setDescription] = useState('');
    const [descriptionAr, setDescriptionAr] = useState('');
    const [notes, setNotes] = useState('');

    // Detail lines
    const [details, setDetails] = useState<DetailLine[]>([]);
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

    // Auto-select current fiscal period on mount (only for new entries)
    useEffect(() => {
        if (open && !isEdit) {
            loadCurrentFiscalPeriod();
        }
    }, [open, isEdit]);

    useEffect(() => {
        if (open) {
            if (entry) {
                loadEntry();
            } else {
                resetForm();
            }
        }
    }, [open, entry]);

    // Auto-fetch next entry number when entry type changes
    useEffect(() => {
        if (!isEdit && entryTypeId && entryType) {
            fetchNextEntryNumber();
        }
    }, [entryTypeId, isEdit]);

    // Check duplicate entry number
    useEffect(() => {
        if (entryNumber && !isEdit && entryTypeId) {
            const timer = setTimeout(() => {
                checkDuplicateEntryNumber();
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [entryNumber, isEdit, entryTypeId]);

    const loadCurrentFiscalPeriod = async () => {
        try {
            const response = await apiClient.get('/FiscalPeriods/current');
            if (response.data && response.data.fiscalPeriodId) {
                setFiscalPeriodId(response.data.fiscalPeriodId);
            }
        } catch (err) {
        }
    };

    const resetForm = () => {
        setEntryTypeId(null);
        setEntryType(null);
        setEntryNumberPrefix('JE-');
        setEntryNumber('');
        setEntryDate(new Date().toISOString().split('T')[0]);
        // Don't reset fiscalPeriodId - keep it from loadCurrentFiscalPeriod
        setDescription('');
        setDescriptionAr('');
        setNotes('');
        setDetails([createNewLine(1)]);
        setError('');
    };

    const loadEntry = () => {
        if (!entry) return;

        setEntryTypeId(entry.entryTypeId);
        setEntryNumberPrefix('');
        setEntryNumber(entry.entryNumber);
        setEntryDate(entry.entryDate.split('T')[0]);
        setFiscalPeriodId(entry.fiscalPeriodId || null);
        setDescription(entry.description || '');
        setDescriptionAr(entry.descriptionAr || '');
        setNotes(entry.notes || '');

        const loadedDetails: DetailLine[] = entry.details.map((detail) => ({
            id: `${detail.entryDetailId}`,
            lineNumber: detail.lineNumber,
            accountId: detail.accountId,
            debit: detail.debit,
            credit: detail.credit,
            costCenterId: detail.costCenterId || null,
            description: detail.description || '',
            notes: detail.notes || '',
            entryDetailId: detail.entryDetailId,
        }));

        setDetails(loadedDetails);
    };

    const fetchNextEntryNumber = async () => {
        if (!entryTypeId) return;
        try {
            const nextNumber =
                await journalEntriesService.getNextEntryNumber(entryTypeId);
            setEntryNumber(nextNumber);
            setError('');
        } catch (err) {
            console.error('Failed to fetch next entry number:', err);
        }
    };

    const checkDuplicateEntryNumber = async () => {
        if (!entryNumber) return;

        try {
            const isDuplicate =
                await journalEntriesService.checkDuplicate(entryNumber);
            if (isDuplicate) {
                setError(
                    isRTL
                        ? `⚠️ رقم القيد ${entryNumber} مستخدم بالفعل - سيتم اقتراح رقم جديد`
                        : `⚠️ Entry number ${entryNumber} already exists - A new number will be suggested`
                );
                if (entryTypeId) {
                    fetchNextEntryNumber();
                }
            }
        } catch (err) {
        }
    };

    const createNewLine = (lineNumber: number): DetailLine => ({
        id: Date.now().toString() + Math.random(),
        lineNumber,
        accountId: null,
        debit: 0,
        credit: 0,
        costCenterId: null,
        description: '',
        notes: '',
    });

    const addNewLine = () => {
        const newLine = createNewLine(details.length + 1);
        setDetails([...details, newLine]);
    };

    const removeLine = (id: string) => {
        if (details.length === 1) {
            setError(
                isRTL
                    ? 'يجب أن يحتوي القيد على سطر واحد على الأقل'
                    : 'Entry must have at least one line'
            );
            return;
        }
        const newDetails = details.filter((d) => d.id !== id);
        newDetails.forEach((d, index) => {
            d.lineNumber = index + 1;
        });
        setDetails(newDetails);
    };

    const updateLine = (id: string, field: keyof DetailLine, value: any) => {
        setDetails(
            details.map((d) => (d.id === id ? { ...d, [field]: value } : d))
        );
    };

    const handleDescriptionChange = (
        value: string,
        field: 'description' | 'descriptionAr'
    ) => {
        if (field === 'description') {
            setDescription(value);
            if (!descriptionAr && value) {
                setDescriptionAr(value);
            }
        } else {
            setDescriptionAr(value);
            if (!description && value) {
                setDescription(value);
            }
        }
    };

    const calculateTotals = () => {
        const totalDebit = details.reduce((sum, d) => sum + (d.debit || 0), 0);
        const totalCredit = details.reduce(
            (sum, d) => sum + (d.credit || 0),
            0
        );
        return { totalDebit, totalCredit };
    };

    const validateForm = async (): Promise<boolean> => {
        if (!isEdit && !entryTypeId) {
            setError(isRTL ? 'نوع القيد مطلوب' : 'Entry type is required');
            return false;
        }

        if (!entryDate) {
            setError(isRTL ? 'التاريخ مطلوب' : 'Date is required');
            return false;
        }

        if (details.length === 0) {
            setError(
                isRTL
                    ? 'يجب إضافة سطر واحد على الأقل'
                    : 'At least one detail line is required'
            );
            return false;
        }

        for (const detail of details) {
            if (!detail.accountId) {
                setError(
                    `${isRTL ? 'الحساب مطلوب في السطر' : 'Account is required in line'} ${detail.lineNumber}`
                );
                return false;
            }

            if (detail.debit === 0 && detail.credit === 0) {
                setError(
                    `${isRTL ? 'يجب إدخال مدين أو دائن في السطر' : 'Debit or Credit is required in line'} ${detail.lineNumber}`
                );
                return false;
            }

            if (detail.debit > 0 && detail.credit > 0) {
                setError(
                    `${isRTL ? 'لا يمكن إدخال مدين ودائن معاً في السطر' : 'Cannot have both Debit and Credit in line'} ${detail.lineNumber}`
                );
                return false;
            }
        }

        const { totalDebit, totalCredit } = calculateTotals();
        // ✅ Validate RequireCostCenter
        try {
            for (const detail of details) {
                if (detail.accountId) {
                    const accountResponse = await apiClient.get(
                        '/ChartOfAccounts/' + detail.accountId
                    );
                    const account = accountResponse.data.data;

                    if (account.requireCostCenter && !detail.costCenterId) {
                        const errorMsg =
                            (isRTL ? 'الحساب' : 'Account') +
                            ' "' +
                            (isRTL
                                ? account.accountNameAr
                                : account.accountName) +
                            '" ' +
                            (isRTL
                                ? 'يتطلب مركز تكلفة في السطر'
                                : 'requires a Cost Center in line') +
                            ' ' +
                            detail.lineNumber;
                        setError(errorMsg);
                        toast.warning(errorMsg);
                        return false;
                    }
                }
            }
        } catch (err) {
            console.error('Error validating accounts:', err);
        }
        if (Math.abs(totalDebit - totalCredit) > 0.001) {
            setError(
                isRTL
                    ? 'القيد غير متوازن - المدين يجب أن يساوي الدائن'
                    : 'Entry is unbalanced - Debit must equal Credit'
            );
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        setError('');

        if (!(await validateForm())) return;

        try {
            setLoading(true);

            if (isEdit) {
                const updateDto: UpdateJournalEntryDto = {
                    journalEntryId: entry!.journalEntryId,
                    entryDate,
                    entryType: entryType?.entryTypeCode || 'JE',
                    fiscalYearId: fiscalPeriodId || undefined,
                    fiscalPeriodId: fiscalPeriodId || undefined,
                    sourceType: 'Manual',
                    sourceDocumentId: undefined,
                    description,
                    descriptionAr,
                    notes,
                    details: details.map((d) => ({
                        entryDetailId: d.entryDetailId,
                        lineNumber: d.lineNumber,
                        accountId: d.accountId!,
                        debit: d.debit,
                        credit: d.credit,
                        costCenterId: d.costCenterId || undefined,
                        description: d.description,
                        notes: d.notes,
                    })),
                };
                await journalEntriesService.update(
                    entry!.journalEntryId,
                    updateDto
                );
            } else {
                const createDto: CreateJournalEntryDto = {
                    entryTypeId: entryTypeId!,
                    entryNumber: entryNumber || undefined,
                    entryDate,
                    fiscalPeriodId: fiscalPeriodId || undefined,
                    sourceType: 'Manual',
                    description,
                    descriptionAr,
                    notes,
                    details: details.map((d) => ({
                        lineNumber: d.lineNumber,
                        accountId: d.accountId!,
                        debit: d.debit,
                        credit: d.credit,
                        costCenterId: d.costCenterId || undefined,
                        description: d.description,
                        notes: d.notes,
                    })),
                };
                await journalEntriesService.create(createDto);
            }

            toast.success(isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
            onSuccess();
            onClose();
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    (isEdit
                        ? isRTL
                            ? 'فشل تحديث القيد'
                            : 'Failed to update journal entry'
                        : isRTL
                          ? 'فشل إنشاء القيد'
                          : 'Failed to create journal entry')
            );
            console.error('Failed to save journal entry:', err);
            toast.error(
                err.response?.data?.message ||
                    (isEdit
                        ? isRTL
                            ? 'فشل تحديث القيد'
                            : 'Failed to update journal entry'
                        : isRTL
                          ? 'فشل إنشاء القيد'
                          : 'Failed to create journal entry')
            );
        } finally {
            setLoading(false);
        }
    };

    const { totalDebit, totalCredit } = calculateTotals();
    const difference = Math.abs(totalDebit - totalCredit);
    const isBalanced = difference < 0.001;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
            <DialogTitle>
                {isEdit
                    ? isRTL
                        ? 'تعديل قيد يومية'
                        : 'Edit Journal Entry'
                    : isRTL
                      ? 'قيد يومية جديد'
                      : 'New Journal Entry'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 2 }}
                        onClose={() => setError('')}
                    >
                        {error}
                    </Alert>
                )}

                {/* Entry Type & Number */}
                <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                        }}
                    >
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 45%', minWidth: 250 }}>
                                <EntryTypeSelector
                                    value={entryTypeId}
                                    onChange={(id, type) => {
                                        setEntryTypeId(id);
                                        setEntryType(type);
                                        if (type) {
                                            setEntryNumberPrefix(
                                                type.prefix + '-'
                                            );
                                        }
                                    }}
                                    required
                                    disabled={isEdit}
                                    onlyManualEntry={true}
                                />
                            </Box>

                            <Box
                                sx={{
                                    flex: '1 1 45%',
                                    minWidth: 250,
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <TextField
                                    label={isRTL ? 'البادئة' : 'Prefix'}
                                    value={isEdit ? '' : entryNumberPrefix}
                                    disabled
                                    sx={{ width: '30%' }}
                                />
                                <TextField
                                    fullWidth
                                    label={isRTL ? 'رقم القيد' : 'Entry Number'}
                                    value={entryNumber}
                                    onChange={(e) =>
                                        setEntryNumber(e.target.value)
                                    }
                                    disabled={isEdit}
                                    required={!isEdit}
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        label={isRTL ? 'التاريخ' : 'Date'}
                                        value={entryDate ? new Date(entryDate) : null}
                                        onChange={(date) => setEntryDate(date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                                        format="dd/MM/yyyy"
                                        views={['year', 'month', 'day']}
                                        openTo="day"
                                        minDate={minDate}
                                        maxDate={maxDate}
                                        slotProps={{ textField: { fullWidth: true, required: true } }}
                                    />
                                </LocalizationProvider>
                            </Box>

                            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                <FiscalYearSelector
                                    value={fiscalPeriodId}
                                    onChange={setFiscalPeriodId}
                                />
                            </Box>

                            <Box sx={{ flex: '1 1 30%', minWidth: 200 }}>
                                <TextField
                                    fullWidth
                                    label={isRTL ? 'الوصف' : 'Description (EN)'}
                                    value={description}
                                    onChange={(e) =>
                                        handleDescriptionChange(
                                            e.target.value,
                                            'description'
                                        )
                                    }
                                />
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 45%', minWidth: 250 }}>
                                <TextField
                                    fullWidth
                                    label={
                                        isRTL
                                            ? 'الوصف بالعربي'
                                            : 'Description (AR)'
                                    }
                                    value={descriptionAr}
                                    onChange={(e) =>
                                        handleDescriptionChange(
                                            e.target.value,
                                            'descriptionAr'
                                        )
                                    }
                                />
                            </Box>

                            <Box sx={{ flex: '1 1 45%', minWidth: 250 }}>
                                <TextField
                                    fullWidth
                                    label={isRTL ? 'ملاحظات' : 'Notes'}
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Details Table */}
                <Box sx={{ mb: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 1,
                        }}
                    >
                        <Typography variant="h6">
                            {isRTL ? 'التفاصيل' : 'Details'}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={addNewLine}
                        >
                            {isRTL ? 'إضافة سطر' : 'Add Line'}
                        </Button>
                    </Box>

                    <Box ref={gridRef}>
                    <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.light' }}>
                                    <TableCell width="3%" sx={{ fontWeight: 'bold', p: 0.5 }}></TableCell>
                                    <TableCell width="4%" sx={{ fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell width="33%" sx={{ fontWeight: 'bold' }}>{isRTL ? 'الحساب' : 'Account'}</TableCell>
                                    <TableCell width="15%" sx={{ fontWeight: 'bold' }}>{isRTL ? 'مدين' : 'Debit'}</TableCell>
                                    <TableCell width="15%" sx={{ fontWeight: 'bold' }}>{isRTL ? 'دائن' : 'Credit'}</TableCell>
                                    <TableCell width="24%" sx={{ fontWeight: 'bold' }}>{isRTL ? 'مركز التكلفة' : 'Cost Center'}</TableCell>
                                    <TableCell width="6%"></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {details.map((detail) => {
                                    const isExpanded = expandedRows.has(detail.id);
                                    return (
                                    <React.Fragment key={detail.id}>
                                        <TableRow>
                                            <TableCell sx={{ p: 0.5 }}>
                                                <IconButton size="small"
                                                    onClick={() => setExpandedRows(prev => {
                                                        const next = new Set(prev);
                                                        next.has(detail.id) ? next.delete(detail.id) : next.add(detail.id);
                                                        return next;
                                                    })}>
                                                    {isExpanded ? <KeyboardArrowUp fontSize="small" /> : <KeyboardArrowDown fontSize="small" />}
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={detail.lineNumber} size="small" color="primary" />
                                            </TableCell>
                                            <TableCell>
                                                <AccountSelector
                                                    value={detail.accountId}
                                                    onChange={(id) => updateLine(detail.id, 'accountId', id)}
                                                    fullWidth
                                                    onlyFinalAccounts={true}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField size="small" fullWidth type="number"
                                                    value={detail.debit || ''}
                                                    onChange={(e) => updateLine(detail.id, 'debit', parseFloat(e.target.value) || 0)}
                                                    inputProps={{ min: 0, step: 0.001 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField size="small" fullWidth type="number"
                                                    value={detail.credit || ''}
                                                    onChange={(e) => updateLine(detail.id, 'credit', parseFloat(e.target.value) || 0)}
                                                    inputProps={{ min: 0, step: 0.001 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <CostCenterSelector
                                                    value={detail.costCenterId}
                                                    onChange={(id) => updateLine(detail.id, 'costCenterId', id)}
                                                    fullWidth
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton size="small" color="error"
                                                    onClick={() => removeLine(detail.id)}
                                                    disabled={details.length === 1}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell colSpan={7} sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }}>
                                                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                    <Box sx={{ py: 1.5, px: 2 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                                                            {isRTL ? 'البيان' : 'Description'}
                                                        </Typography>
                                                        <TextField
                                                            size="small" fullWidth
                                                            value={detail.description}
                                                            onChange={(e) => updateLine(detail.id, 'description', e.target.value)}
                                                            placeholder={isRTL ? 'أدخل وصف البند...' : 'Enter line description...'}
                                                            multiline minRows={1} maxRows={3}
                                                        />
                                                    </Box>
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    </React.Fragment>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    </Box>
                </Box>

                {/* Totals */}
                <Paper
                    variant="outlined"
                    sx={{
                        p: 2,
                        bgcolor: isBalanced ? 'success.light' : 'warning.light',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                            gap: 2,
                        }}
                    >
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {isRTL ? 'إجمالي المدين' : 'Total Debit'}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {totalDebit.toFixed(3)} KWD
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {isRTL ? 'إجمالي الدائن' : 'Total Credit'}
                            </Typography>
                            <Typography variant="h6" fontWeight="bold">
                                {totalCredit.toFixed(3)} KWD
                            </Typography>
                        </Box>
                        <Box>
                            <Typography variant="body2" color="text.secondary">
                                {isRTL ? 'الفرق' : 'Difference'}
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                color={
                                    isBalanced ? 'success.main' : 'error.main'
                                }
                            >
                                {difference.toFixed(3)} KWD
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {!isBalanced && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {isRTL
                            ? '⚠️ القيد غير متوازن - لا يمكن الحفظ حتى يتساوى المدين مع الدائن'
                            : '⚠️ Entry is unbalanced - Cannot save until Debit equals Credit'}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={loading}>
                    {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !isBalanced}
                    size="large"
                >
                    {loading
                        ? isRTL
                            ? 'جاري الحفظ...'
                            : 'Saving...'
                        : isEdit
                          ? isRTL
                              ? 'تحديث'
                              : 'Update'
                          : isRTL
                            ? 'حفظ'
                            : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default JournalEntryDialog;